import { Editor } from "@tinymce/tinymce-react";
import Button from "components/common/Button";
import CustomToast from "components/common/CustomToast";
import Loader from "components/common/Loader";
import VendorAvatar from "components/common/VendorAvatar";
import config from "config/config";
import { useMergedSearchParams } from "helpers/mergeSearchParams";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import { debounce } from "lodash";
import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useParams, useSearchParams } from "react-router-dom";
import { spaceActions } from "redux/Ldb/actions/spaceActions";

type NoteDetailsProps = {
    noteId: string;
    setNoteId: (value: string) => void;
    setDeleteNote: (value: boolean) => void;
};

const NoteDetails = ({ noteId, setNoteId, setDeleteNote }: NoteDetailsProps) => {
    const [searchParams] = useSearchParams();
    const noteIdParams = searchParams.get("noteId");

    const dispatch = useAppDispatch();
    const inputRef = useRef(null);

    const { spaceId } = useParams();
    const { updateSearchParams } = useMergedSearchParams();
    const [updatedName, setUpdatedName] = useState<string>("");
    const [updatedBody, setUpdatedBody] = useState<string>("");
    const [failedToSave, setFailedToSave] = useState<boolean>(false);
    const [updated, setUpdated] = useState<boolean>(false);

    const [note, setNote] = useState<any>(null);
    const {
        fetchSingleSpaceNoteSuccess,
        fetchingSingleSpaceNote,
        updateSpaceNoteSuccess,
        updatingSpaceNote,
        updateSpaceNoteFailure,
    } = useAppSelector((state) => state.space);

    useEffect(() => {
        (inputRef?.current as any)?.focus();
    }, []);

    useEffect(() => {
        if (!noteId || !spaceId || !updated) {
            return;
        }
        dispatch(
            spaceActions.updateSpaceNote(spaceId, noteId, {
                name: updatedName || "Untitled note",
                body: updatedBody || "",
            })
        );
    }, [updatedName, updatedBody, noteId, spaceId, updated]);


    const handleUpdateNoteName = debounce((e) => {
        setUpdatedName(e?.target?.value);
        setUpdatedBody(note?.body);
        setUpdated(true);
    }, 3000);

    useEffect(() => {
        if (Boolean(updateSpaceNoteSuccess)) {
            setFailedToSave(false);
            dispatch(spaceActions.resetUpdateSpaceNote());
        }
    }, [dispatch, updateSpaceNoteSuccess]);

    useEffect(() => {
        if (spaceId && noteIdParams) {
            dispatch(spaceActions.getSpaceSingleNote(spaceId, noteIdParams));
        }
    }, [dispatch, noteIdParams, spaceId]);

    useEffect(() => {
        if (Boolean(fetchSingleSpaceNoteSuccess)) {
            setNote(fetchSingleSpaceNoteSuccess?.data?.note);
            dispatch(spaceActions.resetGetSpaceSingleNote());
        }
    }, [dispatch, fetchSingleSpaceNoteSuccess]);

    useEffect(() => {
        if (Boolean(updateSpaceNoteFailure)) {
            setFailedToSave(true);
            dispatch(spaceActions.resetUpdateSpaceNote());
        }
    }, [dispatch, updateSpaceNoteFailure]);

    const debouncedChangeHandler = useCallback(
        debounce((newValue: string, name: string) => {
            setUpdatedName(name);
            setUpdatedBody(newValue);
            setUpdated(true);
        }, 3000),
        []
    );
    return (
        <div className="">
            {" "}
            <div className="px-[24px] sticky top-0 bg-white z-10 flex items-center justify-between border-b border-b-slate-100 mb-4 pb-2.5">
                <div
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={() => {
                        dispatch(spaceActions.getSpaceNotes(spaceId, 1, 20));

                        updateSearchParams({
                            noteId: "",
                        });
                        setNoteId("");
                    }}
                >
                    <i className="ri-arrow-left-line text-sm text-slate-950 cursor-pointer"></i>
                    <p className="text-sm font-rocGroteskMedium text-slate-950 cursor-pointer">
                        Back to notes
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <i className="ri-cloud-line text-sm text-slate-500"></i>
                    <p className="text-[13px] font-rocGroteskMedium text-slate-500">
                        {failedToSave ? "Not saved" : updatingSpaceNote ? "Saving..." : "Saved"}
                    </p>
                </div>
            </div>
            <div className={`font-rocGroteskMedium  px-[24px] flex flex-col mt-10`}>
                {fetchingSingleSpaceNote ? (
                    <div className="flex-1 flex items-center justify-center h-[200px]">
                        <Loader color="gm-25" size={5} />
                    </div>
                ) : (
                    <div className="flex-1 flex items-center justify-center pb-6">
                        <div className={`flex  justify-center items-center`}>
                            <div className="max-w-[640px] w-full text-start">
                                <div className="space-y-4">
                                    <div className="pb-4 border-b border-b-slate-100">
                                        <div className="flex items-center mb-2">
                                            <p className="text-xs font-rocGroteskMedium text-gm-50 ">
                                                Note name
                                            </p>
                                            <p className="text-sm text-r-50">*</p>
                                        </div>
                                        <input
                                            type="text"
                                            placeholder="Untitled note"
                                            className="!text-2xl font-rocGroteskMedium  placeholder:text-slate-400 !border-none outline-none 
                                w-full caret-slate-700 !leading-6"
                                            ref={inputRef}
                                            name="name"
                                            value={note?.name}
                                            onChange={(e) =>
                                                setNote({ ...note, name: e.target.value })
                                            }
                                            onInput={handleUpdateNoteName}
                                        />
                                    </div>
                                    <div className="pb-4 border-b border-b-slate-100 grid grid-cols-3">
                                        <div className="border-r border-r-slate-100 ">
                                            <p className="text-xs font-rocGroteskMedium text-slate-500 mb-2">
                                                Added by
                                            </p>
                                            <div className="flex items-center gap-2">
                                                <VendorAvatar
                                                    size={20}
                                                    imageSrc={
                                                        note?.createdBy?.ldb?.profile?.avatar || ""
                                                    }
                                                    name={
                                                        note?.createdBy?.ldb?.fullName ||
                                                        note?.createdBy?.ldb?.profile
                                                            ?.businessName ||
                                                        note?.createdBy?.guest?.email
                                                    }
                                                />
                                                <p className="text-sm text-slate-950">
                                                    {note?.createdBy?.ldb?.fullName ||
                                                        note?.createdBy?.ldb?.profile
                                                            ?.businessName ||
                                                        note?.createdBy?.guest?.email}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="border-r border-r-slate-100 pl-4">
                                            <p className="text-xs font-rocGroteskMedium text-slate-500 mb-2">
                                                Created on
                                            </p>
                                            <p className="text-sm text-slate-950">
                                                {moment(note?.createdAt).format("Do MMM YYYY")}
                                            </p>
                                        </div>
                                        <div className="pl-4">
                                            <p className="text-xs font-rocGroteskMedium text-slate-500 mb-2">
                                                Last Edited By
                                            </p>
                                            <div className="flex items-center gap-2">
                                                <VendorAvatar
                                                    size={16}
                                                    imageSrc={
                                                        note?.editedBy?.ldb?.profile?.avatar || ""
                                                    }
                                                    name={
                                                        note?.editedBy?.ldb?.fullName ||
                                                        note?.editedBy?.ldb?.profile
                                                            ?.businessName ||
                                                        note?.editedBy?.guest?.email
                                                    }
                                                />
                                                <p className="text-sm text-slate-950">
                                                    {note?.editedBy?.ldb?.fullName ||
                                                        note?.editedBy?.ldb?.profile
                                                            ?.businessName ||
                                                        note?.editedBy?.guest?.email}
                                                    {` - `}
                                                    {moment(note?.editedAt).format("MMM DD, YYYY")}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="border-b border-b-slate-100">
                                        <p className="text-xs font-rocGroteskMedium text-gm-50 mb-2">
                                            Description / Body
                                        </p>
                                        {/* <textarea
                                            placeholder="Enter your detailed note content here"
                                            className="!text-sm font-rocGroteskMedium  placeholder:text-slate-400 !border-none outline-none 
                                w-full !leading-6 resize-none max-h-[200px]"
                                            name="description"
                                            value={note?.body}
                                            onChange={(e) =>
                                                setNote({ ...note, body: e.target.value })
                                            }
                                            required
                                            onInput={handleUpdateNoteBody}
                                        /> */}
                                        <div className="my-editor-content">
                                            <Editor
                                                apiKey={config.TINYMICE_API}
                                                value={note?.body}
                                                onEditorChange={(newValue, editor) => {
                                                    setNote({ ...note, body: newValue });
                                                    debouncedChangeHandler(
                                                        newValue,
                                                        note?.name || "Untitled note"
                                                    );
                                                }}
                                                init={{
                                                    height: 200,
                                                    menubar: false,
                                                    plugins: [
                                                        "advlist",
                                                        "autolink",
                                                        "lists",
                                                        "link",
                                                        "anchor",
                                                        "searchreplace",
                                                        "visualblocks",
                                                        "fullscreen",
                                                        "insertdatetime",
                                                        "media",
                                                    ],

                                                    placeholder:
                                                        "Enter your detailed note content here",
                                                    // selector: "textarea",
                                                    resize: false,
                                                    branding: false,
                                                    toolbar: false,
                                                    body_class: "",
                                                    content_style:
                                                        "body { font-family:rocGrotesk,Helvetica,Arial,sans-serif; font-size:14px; margin:0px !important; }",
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div className="flex justify-start gap-3">
                                        <Button
                                            btnText={"Copy link"}
                                            btnClassname="!w-fit !px-4 !py-2"
                                            onClick={() => {
                                                navigator.clipboard.writeText(
                                                    `${window.location.origin}/spaces/${spaceId}?tab=4&noteId=${noteId}`
                                                );
                                                toast.custom((t) => (
                                                    <CustomToast
                                                        t={t}
                                                        message={"Link copied to clipboard"}
                                                        type={"success"}
                                                    />
                                                ));
                                            }}
                                        />
                                        <Button
                                            btnText={"Delete note"}
                                            type="button"
                                            btnClassname="!w-fit !bg-n-20 !text-gm-50 !px-4 !py-2"
                                            onClick={() => {
                                                setDeleteNote(true);
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NoteDetails;
